import {
  Alert,
  AlertDescription,
  Button,
  Collapsible,
  HStack,
  Input,
  Link,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Field, Form, Formik, useFormikContext } from "formik"
import { useEffect, useState } from "react"
import { StringParam, useQueryParam } from "use-query-params"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"

const prefix = "order.discount_form"
const translationKeys = {
  placeholder: `${prefix}.placeholder`,
  submit: `${prefix}.submit`,
  invalid: `${prefix}.invalid`,
  discount: `${prefix}.discount`,
  clear: `${prefix}.clear`
}

const schema = z.object({
  code: z.string().min(1)
})
const validate = toFormikValidate(schema)

function FormSubmitter() {
  const { initialValues, handleSubmit } = useFormikContext<{ code: string }>()

  useEffect(() => {
    // on initial mount, let's query for the discount if there is one pulled from the URL search params
    if (initialValues.code) {
      handleSubmit()
    }
  }, [])

  return null
}

type DiscountResult = {
  discount_percent: number
  discount_perc?: number
  currency_display_discount_price: string
  discounted_total_currency_display?: string
  discountMessage?: string
}

export type OrderDiscountFormProps = {
  applyDiscountCode: (code: string) => Promise<DiscountResult>
  clearDiscountCode?: () => void
  searchParamKey?: string
}

function OrderDiscountForm({
  applyDiscountCode,
  searchParamKey = "coupon",
  clearDiscountCode
}: OrderDiscountFormProps) {
  const colorPalette = useColorModeValue("brand", undefined)
  const { t } = useTranslate()
  const [discountCode] = useQueryParam(searchParamKey || "", StringParam)
  const [successfulDiscountCode, setSuccessfulDiscountCode] = useState<
    string | null
  >(null)
  const [discountResult, setDiscountResult] = useState<DiscountResult | null>(
    null
  )
  const [discountError, setDiscountError] = useState<string | null>(null)

  const { open, onClose, onToggle } = useDisclosure({
    defaultOpen: true
  })

  useEffect(() => {
    if (open) {
      setDiscountError(null)
      setDiscountResult(null)
      clearDiscountCode?.()
    }
  }, [open])

  async function handleDiscount(discountCode: string) {
    setDiscountError(null)
    setSuccessfulDiscountCode(null)
    try {
      const discountResult = await applyDiscountCode(discountCode)
      if (discountResult) {
        setDiscountResult(discountResult)
        setSuccessfulDiscountCode(discountCode)
        onClose()
      } else {
        throw new Error(
          t(`${translationKeys.invalid}.The code "{{ code }}" is not valid.`, {
            code: discountCode
          })
        )
      }
    } catch (error) {
      setDiscountError((error as unknown as Error).message)
    }
  }

  const discountPercent =
    discountResult?.discount_percent || discountResult?.discount_perc
  const discountDisplayPrice =
    discountResult?.currency_display_discount_price ||
    discountResult?.discounted_total_currency_display

  // Derived discount message from discount result.
  const discountMessage =
    discountPercent && discountDisplayPrice
      ? `${t(
          `${translationKeys.discount}.Discount applied ({{ perc }}% off). Total after discount: {{ total }}`,
          {
            perc: discountPercent * 100,
            total: discountDisplayPrice,
            // For deprecated "payments.elements_form.discount_applied" translation.
            code: successfulDiscountCode,
            newTotal: discountDisplayPrice
          }
        )}.`
      : null

  const discountSuccess = discountResult
    ? typeof discountResult === "string"
      ? discountResult
      : discountMessage || discountResult.discountMessage || null
    : null

  return (
    <VStack w="100%">
      <Collapsible.Root
        open={open}
        style={{
          width: "100%"
        }}
      >
        <Collapsible.Content
          // Note: forcing overflow visible to ensure input outline isn't clipped.
          // The discount form's use of Collapse doesn't appear to necessitate overflow: hidden.
          overflow="visible"
        >
          <Formik
            initialValues={{ code: discountCode || "" }}
            onSubmit={({ code }) => handleDiscount(code)}
            validate={validate}
            validateOnMount
            enableReinitialize
          >
            {(form) => (
              <Form style={{ width: "100%" }}>
                <FormSubmitter />
                <HStack w="100%">
                  <Field
                    as={Input}
                    name="code"
                    placeholder={t(
                      `${translationKeys.placeholder}.Have a discount code?`
                    )}
                    flexGrow={1}
                    autoFocus
                    borderRadius="40px"
                    variant="subtle"
                  />

                  <Button
                    colorPalette={colorPalette}
                    type="submit"
                    disabled={!form.isValid || form.isSubmitting}
                    loading={form.isSubmitting}
                    cursor="pointer"
                  >
                    {t(`${translationKeys.submit}.Apply Code`)}
                  </Button>
                </HStack>
              </Form>
            )}
          </Formik>
        </Collapsible.Content>
      </Collapsible.Root>
      {!!discountError && (
        <Collapsible.Root open style={{ width: "100%" }}>
          <Collapsible.Content>
            <Alert.Root status="error" variant="subtle" wordBreak="break-word">
              <Alert.Indicator>
                <Pathicon icon="caution-octagon" />
              </Alert.Indicator>
              <Alert.Content>
                <Alert.Title>{discountError}</Alert.Title>
              </Alert.Content>
            </Alert.Root>
          </Collapsible.Content>
        </Collapsible.Root>
      )}
      {discountSuccess ? (
        <Collapsible.Root open style={{ width: "100%" }}>
          <Collapsible.Content>
            <Alert.Root
              status="success"
              variant="subtle"
              wordBreak="break-word"
            >
              <Alert.Indicator>
                <Pathicon icon="check-square" />
              </Alert.Indicator>
              <Alert.Content>
                <Alert.Title>{discountSuccess}</Alert.Title>
                <AlertDescription>
                  <Link asChild variant="underline">
                    <Button
                      colorPalette={colorPalette}
                      variant="plain"
                      mr="auto"
                      onClick={onToggle}
                      p={0}
                    >
                      {t(`${translationKeys.clear}.Try a different code?`)}
                    </Button>
                  </Link>
                </AlertDescription>
              </Alert.Content>
            </Alert.Root>
          </Collapsible.Content>
        </Collapsible.Root>
      ) : null}
    </VStack>
  )
}

OrderDiscountForm.displayName = "OrderDiscountForm"

export default OrderDiscountForm
